import { useEffect, useRef } from 'react';
import uPlot, { type Options, type AlignedData } from 'uplot';
import 'uplot/dist/uPlot.min.css';
import type { InteractionCurve } from './types';

interface Props {
  curve: InteractionCurve | undefined;
  /** demand point (M, P) in kgf-cm and kgf */
  demand?: { M: number; P: number };
  title: string;
  subtitle?: string;
  height?: number;
}

// Tokens
const BG = '#0B1220';
const GRID_MAJOR = 'rgba(148,163,184,0.16)';
const AXIS = '#94A3B8';
const AXIS_TICK = 'rgba(148,163,184,0.45)';
const AXIS_BOLD = '#CBD5E1';
const COLOR_NOMINAL = '#94A3B8';
const COLOR_NOMINAL_FILL = 'rgba(148,163,184,0.06)';
const COLOR_DESIGN = '#3B82F6';
const COLOR_DESIGN_FILL = 'rgba(59,130,246,0.20)';
const COLOR_DEMAND = '#F59E0B';
const COLOR_DEMAND_HALO = 'rgba(245,158,11,0.30)';

function fmtAxis(value: number): string {
  const a = Math.abs(value);
  if (a >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${(value / 1e3).toFixed(0)}k`;
  return `${Math.round(value)}`;
}

function strokeClosedPath(
  ctx: CanvasRenderingContext2D,
  pts: { x: number; y: number }[],
): void {
  if (pts.length === 0) return;
  ctx.beginPath();
  ctx.moveTo(pts[0]!.x, pts[0]!.y);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i]!.x, pts[i]!.y);
  ctx.closePath();
}

export function PMCurve2D({ curve, demand, title, subtitle, height = 260 }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const plotRef = useRef<uPlot | null>(null);

  useEffect(() => {
    if (!ref.current) return;
    if (!curve) {
      plotRef.current?.destroy();
      plotRef.current = null;
      return;
    }

    // The interaction curve is a CLOSED loop (a "C" lying sideways) — for each
    // M there are two P values. uPlot expects strictly increasing X, so we
    // cannot feed it the natural curve as a series (it would zig-zag).
    // Instead we feed it a tiny dummy 2-point series that just sets the scale,
    // and then draw both envelopes ourselves (closed polygons) inside hooks.draw.
    const allM = [
      ...curve.nominal.map((p) => p.M),
      ...curve.design.map((p) => p.M),
      demand?.M ?? 0,
    ];
    const allP = [
      ...curve.nominal.map((p) => p.P),
      ...curve.design.map((p) => p.P),
      demand?.P ?? 0,
    ];
    if (allM.length === 0 || allP.length === 0) return;

    const xMin = Math.min(...allM);
    const xMax = Math.max(...allM);
    const yMin = Math.min(...allP);
    const yMax = Math.max(...allP);
    // 5% padding
    const xPad = (xMax - xMin) * 0.05 || 1;
    const yPad = (yMax - yMin) * 0.05 || 1;

    const data: AlignedData = [
      [xMin - xPad, xMax + xPad],
      [yMin - yPad, yMax + yPad],
    ];

    const opts: Options = {
      width: ref.current.clientWidth,
      height,
      title: undefined,
      legend: { show: false },
      cursor: {
        drag: { x: false, y: false },
        points: { show: false },
      },
      scales: {
        x: { time: false, range: [xMin - xPad, xMax + xPad] },
        y: { range: [yMin - yPad, yMax + yPad] },
      },
      axes: [
        {
          stroke: AXIS,
          grid: { stroke: GRID_MAJOR, width: 1 },
          ticks: { stroke: AXIS_TICK, width: 1, size: 5 },
          font: '10.5px JetBrains Mono, ui-monospace',
          values: (_u, ticks) => ticks.map((t) => fmtAxis(t)),
          space: 60,
          gap: 8,
        },
        {
          stroke: AXIS,
          grid: { stroke: GRID_MAJOR, width: 1 },
          ticks: { stroke: AXIS_TICK, width: 1, size: 5 },
          font: '10.5px JetBrains Mono, ui-monospace',
          values: (_u, ticks) => ticks.map((t) => fmtAxis(t)),
          gap: 8,
        },
      ],
      series: [
        {},
        // Invisible scale-anchor series — uPlot needs at least one to compute axes.
        { stroke: 'transparent', points: { show: false } },
      ],
      hooks: {
        drawClear: [
          (u) => {
            const ctx = u.ctx;
            ctx.save();
            ctx.fillStyle = BG;
            ctx.fillRect(u.bbox.left, u.bbox.top, u.bbox.width, u.bbox.height);
            ctx.restore();
          },
        ],
        draw: [
          (u) => {
            const ctx = u.ctx;

            // --- Origin axes (M=0, P=0) emphasized ------------------------
            const xOrigin = u.valToPos(0, 'x', true);
            const yOrigin = u.valToPos(0, 'y', true);
            ctx.save();
            ctx.strokeStyle = AXIS_BOLD;
            ctx.lineWidth = 1;
            ctx.setLineDash([]);
            if (yOrigin >= u.bbox.top && yOrigin <= u.bbox.top + u.bbox.height) {
              ctx.beginPath();
              ctx.moveTo(u.bbox.left, yOrigin);
              ctx.lineTo(u.bbox.left + u.bbox.width, yOrigin);
              ctx.stroke();
            }
            if (xOrigin >= u.bbox.left && xOrigin <= u.bbox.left + u.bbox.width) {
              ctx.beginPath();
              ctx.moveTo(xOrigin, u.bbox.top);
              ctx.lineTo(xOrigin, u.bbox.top + u.bbox.height);
              ctx.stroke();
            }
            ctx.restore();

            // --- Clip to plot area ---------------------------------------
            ctx.save();
            ctx.beginPath();
            ctx.rect(u.bbox.left, u.bbox.top, u.bbox.width, u.bbox.height);
            ctx.clip();

            // Convert curve points to canvas coordinates (preserves natural order)
            const toCanvas = (pts: { M: number; P: number }[]) =>
              pts.map((p) => ({ x: u.valToPos(p.M, 'x', true), y: u.valToPos(p.P, 'y', true) }));

            const nominalPts = toCanvas(curve.nominal);
            const designPts = toCanvas(curve.design);

            // --- Nominal envelope: dashed gray, faint fill ----------------
            strokeClosedPath(ctx, nominalPts);
            ctx.fillStyle = COLOR_NOMINAL_FILL;
            ctx.fill();
            ctx.setLineDash([4, 3]);
            ctx.strokeStyle = COLOR_NOMINAL;
            ctx.lineWidth = 1.6;
            ctx.stroke();

            // --- Design envelope: solid blue, stronger fill ---------------
            strokeClosedPath(ctx, designPts);
            ctx.fillStyle = COLOR_DESIGN_FILL;
            ctx.fill();
            ctx.setLineDash([]);
            ctx.strokeStyle = COLOR_DESIGN;
            ctx.lineWidth = 2.4;
            ctx.stroke();

            ctx.restore();

            // --- Demand point + crosshair guides + label ------------------
            if (demand) {
              const cx = u.valToPos(demand.M, 'x', true);
              const cy = u.valToPos(demand.P, 'y', true);

              ctx.save();
              ctx.strokeStyle = COLOR_DEMAND;
              ctx.lineWidth = 1;
              ctx.setLineDash([3, 3]);
              ctx.beginPath();
              ctx.moveTo(u.bbox.left, cy);
              ctx.lineTo(cx, cy);
              ctx.moveTo(cx, u.bbox.top + u.bbox.height);
              ctx.lineTo(cx, cy);
              ctx.stroke();
              ctx.setLineDash([]);

              ctx.fillStyle = COLOR_DEMAND_HALO;
              ctx.beginPath();
              ctx.arc(cx, cy, 12, 0, Math.PI * 2);
              ctx.fill();

              ctx.fillStyle = COLOR_DEMAND;
              ctx.strokeStyle = '#0F172A';
              ctx.lineWidth = 1.5;
              ctx.beginPath();
              ctx.arc(cx, cy, 5.5, 0, Math.PI * 2);
              ctx.fill();
              ctx.stroke();

              const label = `(${fmtAxis(demand.M)}, ${fmtAxis(demand.P)})`;
              ctx.font = '10.5px JetBrains Mono, ui-monospace';
              const padX = 6;
              const w = ctx.measureText(label).width + padX * 2;
              const h = 16;
              const px = Math.min(cx + 12, u.bbox.left + u.bbox.width - w - 4);
              const py = Math.max(cy - h - 6, u.bbox.top + 4);
              ctx.fillStyle = '#0F172A';
              ctx.strokeStyle = COLOR_DEMAND;
              ctx.lineWidth = 1;
              roundRect(ctx, px, py, w, h, 3);
              ctx.fill();
              ctx.stroke();
              ctx.fillStyle = COLOR_DEMAND;
              ctx.textBaseline = 'middle';
              ctx.fillText(label, px + padX, py + h / 2);
              ctx.restore();
            }
          },
        ],
      },
    };

    plotRef.current?.destroy();
    plotRef.current = new uPlot(opts, data, ref.current);

    const onResize = () => {
      if (plotRef.current && ref.current) {
        plotRef.current.setSize({ width: ref.current.clientWidth, height });
      }
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      plotRef.current?.destroy();
      plotRef.current = null;
    };
  }, [curve, demand?.M, demand?.P, height]);

  return (
    <div className="flex h-full flex-col">
      {/* Header: title row, then legend row underneath — no fight for space */}
      <div className="mb-1 min-w-0">
        <div
          className="truncate text-[10.5px] font-semibold uppercase tracking-[0.18em]"
          style={{ color: 'var(--text-secondary)' }}
        >
          {title}
        </div>
        {subtitle && (
          <div className="truncate text-[10px]" style={{ color: 'var(--text-muted)' }}>
            {subtitle}
          </div>
        )}
      </div>
      <div className="mb-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px]" style={{ color: 'var(--text-secondary)' }}>
        <Item color={COLOR_NOMINAL} dashed label="Nominal" />
        <Item color={COLOR_DESIGN} label="Diseño (φ)" />
        <Item color={COLOR_DEMAND} dot label="Demanda" />
      </div>
      <div
        ref={ref}
        className="overflow-hidden rounded-[var(--radius-sm)] border"
        style={{
          minHeight: height,
          width: '100%',
          background: BG,
          borderColor: 'var(--border-default)',
        }}
      />
    </div>
  );
}

function Item({
  color,
  label,
  dashed,
  dot,
}: {
  color: string;
  label: string;
  dashed?: boolean;
  dot?: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      {dot ? (
        <span
          className="inline-block h-2.5 w-2.5 rounded-full"
          style={{ background: color, boxShadow: `0 0 0 2px ${COLOR_DEMAND_HALO}` }}
        />
      ) : (
        <svg width="18" height="6" viewBox="0 0 18 6">
          <line
            x1="0"
            y1="3"
            x2="18"
            y2="3"
            stroke={color}
            strokeWidth="2.4"
            strokeDasharray={dashed ? '4 3' : undefined}
            strokeLinecap="round"
          />
        </svg>
      )}
      {label}
    </span>
  );
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
